import React from 'react';
import Typography from '@material-ui/core/Typography';

const MAX_DIAGNOSTIC_LENGTH = 4000;
const VALID_STATUSES = new Set(['passed', 'failed', 'timed_out', 'infrastructure_error', 'skipped']);
const STATUS_LABELS = {
    passed: 'Passed',
    failed: 'Failed',
    timed_out: 'Timed out',
    infrastructure_error: 'Infrastructure error',
    skipped: 'Skipped',
    unknown: 'Unknown',
};
const VALIDATION_TEMPLATE_ID = 'tiny_pinn';
const VALIDATION_SAMPLE_COUNT = 16;
const VALIDATION_MAX_REFERENCE_ERROR = 1e-12;

const boundedText = (value, maxLength = MAX_DIAGNOSTIC_LENGTH) => (
    typeof value === 'string' ? value.slice(0, maxLength) : ''
);

const normaliseGate = (gate) => {
    if (!gate || typeof gate !== 'object') {
        return null;
    }
    const validShape = typeof gate.name === 'string'
        && gate.name.length > 0
        && VALID_STATUSES.has(gate.status)
        && Array.isArray(gate.command)
        && gate.command.every((part) => typeof part === 'string')
        && typeof gate.tool_version === 'string'
        && (gate.exit_code === null || Number.isInteger(gate.exit_code))
        && (gate.duration_ms === null || (Number.isInteger(gate.duration_ms) && gate.duration_ms >= 0))
        && typeof gate.stdout === 'string'
        && typeof gate.stderr === 'string';
    const status = validShape ? gate.status : 'unknown';
    return {
        name: boundedText(gate.name, 120) || 'Validation gate',
        status,
        command: Array.isArray(gate.command) && gate.command.every((part) => typeof part === 'string')
            ? gate.command.slice(0, 16).map((part) => boundedText(part, 200))
            : [],
        tool_version: boundedText(gate.tool_version, 200),
        exit_code: Number.isInteger(gate.exit_code) ? gate.exit_code : null,
        duration_ms: Number.isInteger(gate.duration_ms) && gate.duration_ms >= 0 ? gate.duration_ms : null,
        stdout: boundedText(gate.stdout),
        stderr: boundedText(gate.stderr),
    };
};

const normaliseWorkerEvidence = (evidence) => {
    if (!evidence || typeof evidence !== 'object'
        || evidence.template_id !== VALIDATION_TEMPLATE_ID
        || evidence.sample_count !== VALIDATION_SAMPLE_COUNT
        || evidence.network_isolated !== true
        || typeof evidence.max_abs_reference_error !== 'number'
        || !Number.isFinite(evidence.max_abs_reference_error)
        || evidence.max_abs_reference_error < 0
        || evidence.max_abs_reference_error > VALIDATION_MAX_REFERENCE_ERROR) {
        return null;
    }
    return {
        template_id: VALIDATION_TEMPLATE_ID,
        sample_count: VALIDATION_SAMPLE_COUNT,
        max_abs_reference_error: evidence.max_abs_reference_error,
        network_isolated: true,
    };
};

export const normaliseValidationReport = (report) => {
    if (!report || typeof report !== 'object' || typeof report.passed !== 'boolean' || !Array.isArray(report.gates)) {
        return null;
    }
    const gates = report.gates.slice(0, 20).map(normaliseGate).filter(Boolean);
    const normalised = {
        passed: report.passed && gates.length > 0 && gates.every((gate) => gate.status === 'passed'),
        gates,
    };
    const workerEvidence = normaliseWorkerEvidence(report.worker_evidence);
    if (workerEvidence) {
        normalised.worker_evidence = workerEvidence;
    }
    return normalised;
};

const ValidationReport = ({ classes, report, headingId = 'workflow-validation-heading' }) => {
    const normalised = normaliseValidationReport(report);
    if (!normalised) {
        return null;
    }

    return (
        <section className={classes.section} aria-labelledby={headingId}>
            <Typography id={headingId} variant="subtitle2">
                Validation: {normalised.passed ? 'Passed' : 'Blocked'}
            </Typography>
            {normalised.worker_evidence && (
                <div className={classes.statusList} aria-label="Reviewed worker evidence">
                    <Typography variant="caption" className={classes.muted}>
                        Reviewed worker: {normalised.worker_evidence.template_id}
                        {' · '}{normalised.worker_evidence.sample_count} samples
                        {' · '}max reference error {normalised.worker_evidence.max_abs_reference_error}
                        {' · '}{normalised.worker_evidence.network_isolated ? 'network isolated' : 'network status unknown'}
                    </Typography>
                </div>
            )}
            <div className={classes.statusList}>
                {normalised.gates.map((gate, index) => (
                    <div className={classes.statusRow} key={`${gate.name}-${index}`}>
                        <div>
                            <Typography variant="body2">
                                {gate.name}: {STATUS_LABELS[gate.status]}
                            </Typography>
                            {gate.command.length > 0 && (
                                <Typography variant="caption" component="code" className={classes.muted}>
                                    {gate.command.join(' ')}
                                </Typography>
                            )}
                        </div>
                        <Typography variant="caption" className={classes.muted}>
                            {gate.duration_ms === null ? '' : `${gate.duration_ms} ms`}
                        </Typography>
                    </div>
                ))}
            </div>
            {normalised.gates.map((gate, index) => (gate.stdout || gate.stderr ? (
                <div key={`diagnostics-${gate.name}-${index}`}>
                    <Typography variant="caption" className={classes.muted}>
                        {gate.name} diagnostics
                    </Typography>
                    <pre className={classes.plan}>{[gate.stdout, gate.stderr].filter(Boolean).join('\n')}</pre>
                </div>
            ) : null))}
        </section>
    );
};

export { ValidationReport };
